import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { ColumnDef } from '@tanstack/react-table'
import ItemIcon from 'app/frontend/base/components/ItemIcon'
import Select from 'app/frontend/base/components/Select'
import TeamMemberLink from 'app/frontend/base/components/TeamMemberLink'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  usePageInfo,
  useProfessionRecipes,
  useProfessions,
  useTeamKnownRecipes,
} from 'app/frontend/base/config/queries'
import {
  gameVersionConfig,
  gameVersionHas,
} from 'app/frontend/types/custom/GameVersion'
import { itemQualityClasses } from 'app/frontend/types/custom/ItemQuality'
import { Check, Minus } from 'lucide-react'
import { useMemo, useState } from 'react'
import AuditFilter, { useAuditFilterState } from '../components/AuditFilter'
import { useAudit } from '../context'
import { decodeRecipeIndex } from '../recipeIndex'
import { ColumnDefWithFilters, EnrichedTeamMembership } from '../types'
import { auditTabClassName } from '../utils'

type RecipeRow = wowaudit.Recipe & { knownBy: number }

// The recipe column is sticky, so on small screens it can't take up the whole width
const RECIPE_COLUMN_WIDTH = 'min(400px, 50vw)'

type Holder = {
  member: EnrichedTeamMembership
  knownRecipeIds: Set<number>
  skill?: [number, number]
}

// A character only keeps their highest rank of a ranked recipe, so 0 means not known
const knownRank = (holder: Holder, recipe: wowaudit.Recipe) => {
  const rankIds = recipe.rankIds ?? [recipe.id]
  for (let rank = rankIds.length; rank > 0; rank--) {
    if (holder.knownRecipeIds.has(rankIds[rank - 1])) return rank
  }
  return 0
}

const KnownRank = ({ rank, maxRank }: { rank: number; maxRank?: number }) => {
  if (!rank) return <Minus className='w-4 h-4 mx-auto opacity-20' />
  if (!maxRank) {
    return <Check className='w-4 h-4 mx-auto text-success-foreground' />
  }

  return (
    <span
      className={cn(
        'font-mono',
        rank === maxRank
          ? 'text-success-foreground'
          : 'text-warning-foreground',
      )}
    >
      R{rank}
    </span>
  )
}

const RecipeName = ({
  recipe,
  wowheadPrefix,
}: {
  recipe: RecipeRow
  wowheadPrefix: string
}) => (
  <div className='flex items-center gap-3'>
    <a
      href={`https://wowhead.com/${wowheadPrefix}spell=${recipe.spellId}`}
      target='_blank'
      rel='noreferrer'
      className='flex items-center gap-2 min-w-0 text-left'
    >
      {recipe.icon ? (
        <ItemIcon name={recipe.icon} className='w-6 h-6 shrink-0' />
      ) : (
        <img
          src={recipe.iconUrl}
          alt=''
          className='w-6 h-6 shrink-0 rounded-md'
        />
      )}
      <span
        className={cn(
          'truncate',
          recipe.quality != null && itemQualityClasses[recipe.quality],
        )}
      >
        {recipe.name}
      </span>
    </a>
    <span
      className={cn(
        'ml-auto shrink-0 font-mono text-base',
        recipe.knownBy > 0
          ? 'text-success-foreground'
          : 'text-destructive-foreground',
      )}
    >
      {recipe.knownBy}
    </span>
  </div>
)

const HolderProgress = ({
  skill,
  knownFraction,
}: {
  skill?: [number, number]
  knownFraction?: number
}) => (
  <span className='flex items-center gap-1.5 font-mono text-xs text-muted-foreground'>
    <span
      className={cn(skill && skill[0] >= skill[1] && 'text-success-foreground')}
    >
      {skill ? skill[0] : '-'}
    </span>
    <span className='opacity-40'>·</span>
    <span className={cn(knownFraction === 1 && 'text-success-foreground')}>
      {knownFraction == null ? '-' : `${Math.floor(knownFraction * 100)}%`}
    </span>
  </span>
)

export const useProfessionsColumns = (): ColumnDefWithFilters<RecipeRow> => {
  const { team } = useTeam()
  const { members, visibleMembers } = useAudit()
  const allMembers = members ?? visibleMembers
  const {
    data: { currentExpansion, gameVersion },
  } = usePageInfo()
  const wowheadPrefix = gameVersionConfig(gameVersion).wowhead_prefix
  const { data: professions, isPending: loadingProfessions } = useProfessions()
  const { data: knownRecipesByCharacter, isPending: loadingKnownRecipes } =
    useTeamKnownRecipes(team.id)

  const { value: professionParam, setValue: setProfessionParam } =
    useAuditFilterState<string>('profession', '')
  const { value: expansionParam, setValue: setExpansion } =
    useAuditFilterState<string>('recipeExpansion', currentExpansion.name)
  const { value: categoryParam, setValue: setCategory } = useAuditFilterState<
    string | undefined
  >('recipeCategory', undefined)
  const [search, setSearch] = useState('')

  const holderCountByProfession = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const member of allMembers) {
      const known = knownRecipesByCharacter?.[member.characterReference.id]
      for (const professionId of Object.keys(known ?? {})) {
        counts[professionId] = (counts[professionId] ?? 0) + 1
      }
    }
    return counts
  }, [allMembers, knownRecipesByCharacter])

  const profession = useMemo(() => {
    if (!professions?.length) return undefined

    return (
      professions.find((p) => p.id.toString() === professionParam) ??
      professions.find((p) => holderCountByProfession[p.id]) ??
      professions[0]
    )
  }, [holderCountByProfession, professionParam, professions])

  const { data: recipes, isPending: loadingRecipes } = useProfessionRecipes(
    profession?.id,
  )

  // An expansion the profession has no skill tier for (like one saved for another profession) falls
  // back to the newest tier, which is also the only tier of professions without an expansion filter
  const skillTiers = profession?.skillTiers ?? []
  const singleSkillTier = skillTiers.length === 1
  const skillTier =
    skillTiers.find((tier) => tier.expansion === expansionParam) ??
    skillTiers[skillTiers.length - 1]
  const expansion = skillTier?.expansion ?? expansionParam

  // Ordered by recipes known across all expansions, so columns don't move when filters change
  const holders: Holder[] = useMemo(() => {
    if (!profession) return []

    return allMembers
      .flatMap((member) => {
        const known =
          knownRecipesByCharacter?.[member.characterReference.id]?.[
            profession.id
          ]
        if (!known) return []

        const holder: Holder = {
          member,
          knownRecipeIds: decodeRecipeIndex(known.r),
          skill: skillTier ? known.s[skillTier.id] : undefined,
        }
        const knownCount = (recipes ?? []).filter((recipe) =>
          knownRank(holder, recipe),
        ).length
        return { holder, knownCount }
      })
      .sort(
        (a, b) =>
          b.knownCount - a.knownCount ||
          a.holder.member.characterReference.name.localeCompare(
            b.holder.member.characterReference.name,
          ),
      )
      .map(({ holder }) => holder)
  }, [allMembers, knownRecipesByCharacter, profession, recipes, skillTier])

  const expansionRecipes = useMemo(
    () => (recipes ?? []).filter((recipe) => recipe.expansion === expansion),
    [expansion, recipes],
  )

  const categories = useMemo(
    () => [...new Set(expansionRecipes.map((recipe) => recipe.category))],
    [expansionRecipes],
  )
  // A category saved for another profession or expansion would hide every row
  const category =
    categoryParam && categories.includes(categoryParam)
      ? categoryParam
      : undefined

  const categoryRecipes = useMemo(
    () =>
      expansionRecipes.filter(
        (recipe) => !category || recipe.category === category,
      ),
    [category, expansionRecipes],
  )

  const rows: RecipeRow[] = useMemo(() => {
    const term = search.trim().toLowerCase()
    return categoryRecipes
      .filter((recipe) => !term || recipe.name.toLowerCase().includes(term))
      .map((recipe) => ({
        ...recipe,
        knownBy: holders.filter((holder) => knownRank(holder, recipe)).length,
      }))
  }, [categoryRecipes, holders, search])

  const teamKnownFraction = categoryRecipes.length
    ? categoryRecipes.filter((recipe) =>
        holders.some((holder) => knownRank(holder, recipe)),
      ).length / categoryRecipes.length
    : undefined

  const columns: ColumnDef<RecipeRow>[] = useMemo(
    () => [
      {
        id: 'recipe',
        accessorFn: (row) => row.name,
        size: 400,
        meta: {
          className: 'text-left',
          style: {
            minWidth: RECIPE_COLUMN_WIDTH,
            maxWidth: RECIPE_COLUMN_WIDTH,
            width: RECIPE_COLUMN_WIDTH,
          },
        },
        ...genericSorting<RecipeRow>('text', null),
        header: ({ column }) => (
          <div className='flex items-center gap-3'>
            <span
              className='cursor-pointer'
              onClick={() => column.toggleSorting()}
            >
              {profession ? `${profession.name} Recipes` : 'Recipes'}
            </span>
            {teamKnownFraction != null ? (
              <span
                className={cn(
                  'ml-auto font-mono text-base font-normal',
                  teamKnownFraction === 1
                    ? 'text-success-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {Math.floor(teamKnownFraction * 100)}%
              </span>
            ) : null}
          </div>
        ),
        cell: ({ row }) => (
          <RecipeName recipe={row.original} wowheadPrefix={wowheadPrefix} />
        ),
      },
      ...holders.map(
        (holder): ColumnDef<RecipeRow> => ({
          id: `member_${holder.member.id}`,
          accessorFn: (row) => knownRank(holder, row),
          size: 110,
          ...genericSorting<RecipeRow>(
            'basic',
            <div className='flex flex-col items-center gap-0.5'>
              <TeamMemberLink
                member={holder.member}
                iconType='none'
                rankColorPresentationOverride='hidden'
                disableNavigation
              />
              <HolderProgress
                skill={holder.skill}
                knownFraction={
                  categoryRecipes.length
                    ? categoryRecipes.filter((recipe) =>
                        knownRank(holder, recipe),
                      ).length / categoryRecipes.length
                    : undefined
                }
              />
            </div>,
            true,
          ),
          cell: ({ row }) => (
            <KnownRank
              rank={knownRank(holder, row.original)}
              maxRank={row.original.rankIds?.length}
            />
          ),
        }),
      ),
      // Without a size this column takes up the leftover table width, so the sized columns don't stretch
      {
        id: 'spacer',
        enableSorting: false,
        header: () => null,
        cell: () => null,
      },
    ],
    [categoryRecipes, holders, profession, teamKnownFraction, wowheadPrefix],
  )

  // Skill tiers are ordered from oldest to newest expansion
  const expansionOptions = useMemo(
    () =>
      (profession?.skillTiers ?? []).map((tier, index) => ({
        label: tier.expansion,
        value: tier.expansion,
        sortLabel: index.toString().padStart(3, '0'),
      })),
    [profession],
  )

  const categoryOptions = useMemo(
    () => categories.map((name) => ({ label: name, value: name })),
    [categories],
  )

  const filters = useMemo(
    () => [
      ...(singleSkillTier
        ? []
        : [
            <AuditFilter key='expansion' label='Expansion'>
              <Select
                className='w-56'
                value={expansion}
                setValue={(value) =>
                  setExpansion(value ?? currentExpansion.name)
                }
                options={expansionOptions}
                sortDirection='desc'
              />
            </AuditFilter>,
          ]),
      <AuditFilter key='category' label='Category'>
        <Select
          className='w-64'
          value={category}
          setValue={setCategory}
          options={categoryOptions}
          placeholder='All categories'
          isClearable
          isSearchable
        />
      </AuditFilter>,
      <AuditFilter key='search' label='Search'>
        <Input
          className='w-60'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search...'
        />
      </AuditFilter>,
    ],
    [
      category,
      categoryOptions,
      currentExpansion.name,
      expansion,
      expansionOptions,
      search,
      setCategory,
      setExpansion,
      singleSkillTier,
    ],
  )

  const subNavigation = useMemo(
    () =>
      professions?.length ? (
        <Tabs
          value={profession?.id.toString()}
          onValueChange={setProfessionParam}
        >
          <TabsList className='mx-1 mt-1.5 w-[calc(100%-0.5rem)] h-auto flex flex-wrap justify-start gap-1 p-1 border-0 rounded-md rounded-tr-none bg-muted hover:bg-muted'>
            {professions.map((p) => {
              const crafters = holderCountByProfession[p.id] ?? 0
              return (
                <TabsTrigger
                  key={p.id}
                  value={p.id.toString()}
                  className={cn(auditTabClassName, 'px-1.5')}
                >
                  <div className='flex items-center gap-1'>
                    {p.icon ? (
                      <img src={p.icon} alt='' className='w-4 h-4 rounded-sm' />
                    ) : null}
                    {p.name}
                  </div>
                  <div
                    className={cn(
                      'text-xs font-normal',
                      crafters
                        ? 'text-muted-foreground'
                        : 'text-warning-foreground',
                    )}
                  >
                    {crafters} {crafters === 1 ? 'crafter' : 'crafters'}
                  </div>
                </TabsTrigger>
              )
            })}
          </TabsList>
        </Tabs>
      ) : null,
    [holderCountByProfession, profession, professions, setProfessionParam],
  )

  return {
    columns,
    data: rows,
    filters,
    subNavigation,
    initialSortBy: [{ id: 'recipe', desc: false }],
    showDefaultMemberColumn: false,
    showPendingDataMessage: false,
    hideRankFilter: true,
    loading:
      loadingProfessions ||
      loadingKnownRecipes ||
      (!!profession && loadingRecipes),
    emptyMessage: profession
      ? search.trim() || category
        ? 'No recipes match the current filters.'
        : `No ${profession.name} recipes found for ${expansion}.`
      : undefined,
    tableMessage:
      profession && !holders.length ? (
        <span className='text-warning-foreground'>
          Nobody on this team has {profession.name} yet.{' '}
          {gameVersionHas('blizzard_api', gameVersion) ? (
            "Known recipes show up after each character's next refresh."
          ) : (
            <>
              Known recipes show up once a character&apos;s data is synced
              through the{' '}
              <a
                href='https://www.curseforge.com/wow/addons/wowaudit-companion'
                target='_blank'
                rel='noopener noreferrer'
                className='text-primary hover:text-primary/90'
              >
                companion addon
              </a>
              .
            </>
          )}
        </span>
      ) : undefined,
  }
}
